import dayjs from "dayjs";

import { requiresEntity, requiresVendor, entityTypeForLine } from "./smartUploadLineRequirements";

// Mirrors functions/utils/smart-upload/buildSmartUploadTransaction.js's
// exported MANUAL_PASSTHROUGH_FIELDS. The front/back split means this file
// can't import the server module directly, so the list is duplicated here --
// keep both in sync by hand; a drift means a form field silently stops
// surviving the commit (ticket #42 Technical Design, buildSmartUploadTransaction.js).
export const MANUAL_PASSTHROUGH_FIELDS = [
  "inputSubcategory",
  "scopeThreeCategory",
  "purchasedWaterTonsCo2e",
  "wastewaterTonsCo2e",
  "amount",
  "originalAmount",
  "originalCurrency",
  "exchangeRate",
  "description",
  "employees",
  "taggedEmployeesIds",
  "event"
];

const pick = (obj = {}, keys) =>
  keys.reduce((picked, key) => (obj[key] !== undefined ? { ...picked, [key]: obj[key] } : picked), {});

// Each per-subcategory input hook (inputs.js) reads/writes its own quantity
// field(s) on `emissionData` -- there's no single generic quantity key, so
// the prefill helper and its inverse (used when mapping a saved transaction
// back to a manual line) both key off the subcategory to pick the right ones.
const QUANTITY_PREFILL_MAP = {
  electricity: (line) =>
    line?.quantity_final != null ? { electricKwh: line.quantity_final } : {},
  gas: (line) =>
    line?.quantity_final != null
      ? { gasUnitValue: line.quantity_final, gasUnit: line.unit_final || "therms" }
      : {},
  "company-vehicles": (line) =>
    line?.quantity_final != null
      ? { fuelVolume: line.quantity_final, volumeUnit: line.unit_final }
      : {},
  fuel: (line) =>
    line?.quantity_final != null
      ? { fuelVolume: line.quantity_final, volumeUnit: line.unit_final }
      : {}
};

const SPEND_PREFILL_SUBCATEGORIES = new Set(["spend-based"]);

const quantityPrefillFor = (subcategory, line) => {
  if (QUANTITY_PREFILL_MAP[subcategory]) {
    return QUANTITY_PREFILL_MAP[subcategory](line);
  }
  if (SPEND_PREFILL_SUBCATEGORIES.has(subcategory) && line?.quantity_final != null) {
    return { amount: line.quantity_final };
  }
  return {};
};

const inverseQuantityFieldsFor = (subcategory, transaction) => {
  if (subcategory === "electricity" && transaction.electricKwh != null) {
    return { quantity_final: transaction.electricKwh, unit_final: "kwh" };
  }
  if (subcategory === "gas" && transaction.gasUnitValue != null) {
    return { quantity_final: transaction.gasUnitValue, unit_final: transaction.gasUnit || "therms" };
  }
  if (
    ["company-vehicles", "fuel"].includes(subcategory) &&
    transaction.fuelVolume != null
  ) {
    return { quantity_final: transaction.fuelVolume, unit_final: transaction.volumeUnit };
  }
  if (SPEND_PREFILL_SUBCATEGORIES.has(subcategory) && transaction.amount != null) {
    return { quantity_final: transaction.amount, unit_final: "usd" };
  }
  return {};
};

const entityPrefillFrom = (allocatedEntity, entities = []) => {
  const match = entities.find(({ id }) => id === allocatedEntity?.entity_id);
  if (!match) {
    return null;
  }
  return { id: match.id, name: match.name };
};

const parseablePeriodDate = (line) => {
  const candidate = line?.period_end_final ?? line?.period_start_final;
  const parsed = candidate ? dayjs(candidate) : null;
  return parsed && parsed.isValid() ? parsed.toDate() : null;
};

// The only prefill for a row with no inference at all (a failed file, or an
// error row) is the entity/vendor context -- the date is left `null`
// (never guessed, Design Intent 1.8) and the form's own today-default is
// overridden by that explicit null (ManualTransactionsInput's
// hasInitialDate check).
export const buildManualFallbackPrefill = ({ task, row, line, entities = [] } = {}) => {
  const subcategory = line?.subcategory_final ?? line?.subcategory_ai ?? null;
  const suggestedSubcategories = !subcategory
    ? (line?.taxonomy_candidates || []).map((candidate) => candidate?.label).filter(Boolean)
    : [];

  const date = parseablePeriodDate(line);

  const allocatedEntity =
    line?.allocated_entity_final ||
    (task?.entity_context?.entity_type && task?.entity_context?.entity_id
      ? { entity_type: task.entity_context.entity_type, entity_id: task.entity_context.entity_id }
      : null);
  const prefilledEntity = entityPrefillFrom(allocatedEntity, entities);
  const entityKey = allocatedEntity?.entity_type === "vehicles" ? "vehicle" : "office";

  const vendorName = line?.vendor_name_final ?? line?.vendor_name_ai ?? row?.issuer_name ?? null;
  const isVendorAccepted =
    line?.vendor_match_ai?.isConfirmed || line?.vendor_match_accepted;
  const vendorId = isVendorAccepted ? line?.vendor_match_ai?.vendorId : null;

  const startingTransactionProps = {
    source: "smart-upload-manual-entry",
    ...(subcategory ? { subcategory } : {}),
    date,
    ...(subcategory ? { status: "confirmed" } : {}),
    ...(prefilledEntity ? { [entityKey]: prefilledEntity } : {}),
    ...(vendorName ? { vendorName } : {}),
    ...(vendorId ? { vendorId } : {}),
    ...quantityPrefillFor(subcategory, line)
  };

  return { startingTransactionProps, suggestedSubcategories, prefilledEntity };
};

const allocatedEntityFor = ({ office, vehicle }) => {
  if (vehicle?.id) {
    return { entity_type: "vehicles", entity_id: vehicle.id };
  }
  if (office?.id) {
    return { entity_type: "offices", entity_id: office.id };
  }
  return null;
};

// Replaces mapManualTransactionToLine.js: the same shape, except
// precomputed_transaction_fields now carries only the fields the commit path
// actually reads (MANUAL_PASSTHROUGH_FIELDS) instead of the whole saved
// transaction object, so an unrelated/stale field on the form's output can
// never reach appendManualLine's new unknown-key rejection.
export const manualLineFromTransaction = ({ transaction } = {}) => {
  const { subcategory, date, tonsCo2e, emissionData, vendorId, vendorName } =
    transaction || {};
  const periodDate = dayjs(date).format("YYYY-MM-DD");
  const allocatedEntity = allocatedEntityFor(transaction || {});

  return {
    subcategory_final: subcategory,
    period_start_final: periodDate,
    period_end_final: periodDate,
    ...inverseQuantityFieldsFor(subcategory, transaction || {}),
    precomputed_tons_co2e: tonsCo2e,
    precomputed_emission_data: emissionData || {},
    precomputed_transaction_fields: pick(transaction, MANUAL_PASSTHROUGH_FIELDS),
    ...(vendorId ? { vendor_id: vendorId, vendor_name_final: vendorName || null } : {}),
    ...(allocatedEntity ? { allocated_entity_final: allocatedEntity } : {})
  };
};

export const requirementsForTransaction = ({ row, transaction } = {}) => {
  const documentFamily = row?.document_family;
  const subcategory = transaction?.subcategory;
  return {
    requiresEntity: requiresEntity({ documentFamily, subcategory }),
    entityType: entityTypeForLine({ documentFamily, subcategory }),
    requiresVendor: requiresVendor({
      documentFamily,
      unit: transaction?.unit,
      subcategory
    })
  };
};
